import { useEffect, useState } from "react";

import { API_URL } from "src/service/config";
import { IPaginator } from "src/types";
import LanguageFilter from "../Lesson/LessonFilter";
import Layout from "@components/layout";
import PaginatorBtn from "@components/pagination/paginator.button";
import VideoCardComponent from "./VideoCardComponent";
import { getFromMemory } from "@utils/storage.utils";
import { getRequest } from "src/service/api-services/api-services";

export interface Video {
  id: string;
  code: string;
  translation: string;
  language: string;
  localText: string;
  url: string;
}

export interface VideoApiResponse {
  message: string;
  status: string;
  result: {
    pagination: IPaginator;
    videos: Video[];
  };
}

function ListVideocard() {
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    getFromMemory("language") || "",
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [paginator, setPaginatorData] = useState<IPaginator>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [videos, setVideos] = useState<Video[]>([]);
  const [openVideoCode, setOpenVideoCode] = useState<string | null>(null);

  const [query, setQuery] = useState("");

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedLanguage) params.append("language", selectedLanguage);
        if (query) params.append("search", query);
        params.append("page", String(currentPage));
        params.append("limit", "50");

        const response = await getRequest<VideoApiResponse>(
          `${API_URL}/videos?${params.toString()}`,
        );
        setVideos(response.result.videos);
        setPaginatorData(response.result.pagination);
        setError(undefined);
      } catch (error) {
        setError(error instanceof Error ? error.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [query, currentPage, selectedLanguage]);

  return (
    <Layout>
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-extrabold text-center mb-10 text-brand">
            Video Assets
          </h1>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <input
              className="w-full md:w-1/2 px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="Search for a video..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <LanguageFilter
              selectedLanguage={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
            />
          </div>

          <div className="flex flex-row">
            <div className="mr-2">
              <button
                onClick={() =>
                  (window.location.href = `/dashboard/asset/declear/video`)
                }
                className="bg-brand text-white px-4 py-2 rounded-md hover:bg-brand-dark transition"
              >
                Declear Assets
              </button>
            </div>
            <div className="">
              <button
                onClick={() =>
                  (window.location.href = `/dashboard/assets/upload`)
                }
                className="bg-brand text-white px-4 py-2 rounded-md hover:bg-brand-dark transition"
              >
                Upload Content
              </button>
            </div>
          </div>

          {loading && (
            <p className="text-center text-gray-500">Loading videos...</p>
          )}

          {!loading && error && (
            <p className="text-center text-red-500">{error}</p>
          )}

          {!loading && !error && videos.length === 0 && (
            <p className="text-center text-gray-600">No videos available.</p>
          )}

          {!loading && !error && videos.length > 0 && (
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 mt-8">
              {videos.map((video, index) => (
                <VideoCardComponent
                  key={index}
                  video_url={video.url}
                  id={video.id}
                  videoCode={video.code}
                  localText={video.localText}
                  translation={video.translation}
                  onVideoChange={(val) =>
                    setOpenVideoCode(val ? video.code : null)
                  }
                  IsVideoSelected={openVideoCode === video.code}
                />
              ))}
            </section>
          )}
        </div>
        {paginator && !loading && (
          <PaginatorBtn setCurrentPage={setCurrentPage} paginator={paginator} />
        )}
      </div>
    </Layout>
  );
}

export default ListVideocard;
